import React, { PureComponent, Fragment } from 'react';
import { Card, CardBody, Col, Row, Table } from 'reactstrap';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import Panel from '../../../../../shared/components/Panel';
import ToolTip from '../../../../../shared/components/ToolTip';
import LinearLoading from '../../../../../shared/components/LinearLoading';
import { getDefaultChartColors } from '../../../../../shared/helpers';
import { _formatNumber } from '../../../../../shared/helpers/utils';
import { _axiosCall } from '../../../../../shared/helpers/apicalls';
import {
	SE_CORE,
	SE_FL,
	SE_BEDROOMS,
	SE_SOURCE_CREDIT,
	_seCandidateSlugs,
	_sePickArea,
	_sePriorYearMonth,
	_seMonthLabel,
	_seMonthForDate,
	_seAreaLabel,
	_sePctChange,
} from '../../../../../shared/helpers/streetEasy';

const apiURL = process.env.API_ROOT + '/query/';
const CHART_YEARS = 5;

/**
 * Neighborhood rental market, from StreetEasy's monthly data.
 *
 * This panel exists because the building itself cannot answer the question. Of 90,826 rental
 * buildings, 77% have no unit with a rent listed in the last 24 months and the median among those
 * that do is 4 units — so a building-level median asking rent was a median of two or three
 * listings, and the by-bedroom and PSF cuts of it were thinner still. Those charts are gone; this
 * gives the reader the market the building sits in, measured on tens of thousands of listings.
 *
 * Nothing here is building-specific: every figure is the neighborhood (or the borough, when
 * StreetEasy publishes nothing for the neighborhood).
 */
class NeighborhoodRentMarket extends PureComponent {
	state = { data: null, loading: true };

	componentDidMount() {
		this._fetch();
	}

	componentDidUpdate(prevProps) {
		const prevKey = prevProps.buildingRes && prevProps.buildingRes.key;
		const key = this.props.buildingRes && this.props.buildingRes.key;
		if (prevKey !== key) this._fetch();
	}

	_searchTags = () => {
		const { buildingRes } = this.props;
		if (!buildingRes) return [];
		const hood = Array.isArray(buildingRes.neighborhood) && buildingRes.neighborhood.length > 0
			? buildingRes.neighborhood[0]
			: buildingRes.neighborhood;
		if (hood && buildingRes.borough) {
			return [{ type: 'neighborhood', data: { hood: String(hood).toLowerCase(), boro: String(buildingRes.borough).toLowerCase() } }];
		}
		if (buildingRes.borough) return [{ type: 'borough', data: String(buildingRes.borough).toLowerCase() }];
		return [];
	};

	_fetch = async () => {
		const tags = this._searchTags();
		const candidates = _seCandidateSlugs(tags);
		const endMonth = _seMonthForDate(new Date());
		const startYear = parseInt(endMonth.slice(0, 4), 10) - CHART_YEARS;

		this.setState({ loading: true });
		try {
			const res = await _axiosCall({
				url: apiURL + SE_CORE + '?cache=60',
				method: 'post',
				query: {
					q: `{!terms f=area_slug}${candidates.join(',')}`,
					fq: [`month:[${startYear}-01-01T00:00:00Z TO ${endMonth}-01T23:59:59Z]`],
					fl: SE_FL,
					sort: 'month asc',
					rows: 1000,
					wt: 'json',
				},
			});
			const docs = (res && res.docs) || [];
			const area = _sePickArea(docs, candidates);
			if (!area) {
				this.setState({ data: false, loading: false });
				return;
			}
			const months = docs.filter(d => d.area_slug === area.area_slug);
			const current = months[months.length - 1];
			const prior = months.filter(d => d.month_str === _sePriorYearMonth(current.month_str))[0] || false;
			this.setState({
				data: { area, months, current, prior, label: _seAreaLabel(area) },
				loading: false,
			});
		} catch (e) {
			this.setState({ data: false, loading: false });
		}
	};

	render() {
		const { data, loading } = this.state;
		// The rental summary renders its own loading bar for the unit-mix stats directly below this
		// panel, and both fetches are in flight on first paint. Defer to that one rather than
		// stacking a second identical bar; show ours only when it would be the only indicator.
		if (loading) return this.props.rentalStatsFetchInProgress ? null : <LinearLoading />;
		if (!data || !data.current) return null;

		const { current, prior, months, label } = data;
		const money = v => (v > 0 ? `$${_formatNumber(Math.round(v))}` : '--');
		const change = (field) => {
			const pct = _sePctChange(current[field], prior && prior[field]);
			if (pct === null) return <span>--</span>;
			return (
				<span style={{ color: pct > 0 ? '#4ce1b6' : pct < 0 ? '#ff4861' : 'inherit' }}>
					{pct > 0 ? '+' : ''}{pct}%
				</span>
			);
		};

		const chartData = months
			.filter(m => m.median_asking_rent_all > 0)
			.map(m => ({ name: _seMonthLabel(m.month_str), rent: Math.round(m.median_asking_rent_all) }));

		const monthLabel = _seMonthLabel(current.month_str);
		const priorLabel = prior ? _seMonthLabel(prior.month_str) : '';

		return (
			<Fragment>
				<Row>
					<Col md={12}>
						<Card>
							<CardBody>
								<div className="card__title">
									<h5 className="bold-text">
										{label} Rental Market
										<ToolTip title={'Neighborhood-level asking rents from StreetEasy, published monthly. Building-level rents are not shown: most rental buildings have too few listed units to support a median.'} />
									</h5>
									<h5 className="subhead">
										{monthLabel}{priorLabel ? ` vs. ${priorLabel}` : ''} · all rental listings in {label}, not this building
									</h5>
								</div>
								<Row>
									<Col md={4} xs={6}>
										<h5 className="dashboard__booking-total-title">{money(current.median_asking_rent_all)}</h5>
										<h5 className="dashboard__booking-total-description">
											Median Asking Rent {change('median_asking_rent_all')}
										</h5>
									</Col>
									<Col md={4} xs={6}>
										<h5 className="dashboard__booking-total-title">{current.rental_inventory_all > 0 ? _formatNumber(current.rental_inventory_all) : '--'}</h5>
										<h5 className="dashboard__booking-total-description">
											Listings on Market {change('rental_inventory_all')}
										</h5>
									</Col>
									<Col md={4} xs={6}>
										<h5 className="dashboard__booking-total-title">{current.discount_share_all > 0 ? `${Math.round(current.discount_share_all * 100)}%` : '--'}</h5>
										<h5 className="dashboard__booking-total-description">Share With a Price Cut</h5>
									</Col>
								</Row>
							</CardBody>
						</Card>
					</Col>
				</Row>

				<Row>
					<Panel lg={12} xl={6} md={12} title={<Fragment>{label} — Median Asking Rent by Bedroom <ToolTip title={`Medians across all rental listings in ${label} for ${monthLabel}, against the same month a year earlier. Not specific to this building.`} /></Fragment>}>
						<Table responsive striped className="table--bordered dashboard__audience-table">
							<thead>
								<tr>
									<th>Unit Type</th>
									<th>{monthLabel}</th>
									<th>{priorLabel || 'Prior Year'}</th>
									<th>Change</th>
									<th>Listings</th>
								</tr>
							</thead>
							<tbody>
								{SE_BEDROOMS.map((bed, i) => (
									<tr key={bed.key}>
										<td>
											<span style={{ backgroundColor: getDefaultChartColors[i % getDefaultChartColors.length], width: '10px', height: '10px', display: 'inline-block' }} />
											{' '}{bed.label}
										</td>
										<td>{money(current[`median_asking_rent_${bed.key}`])}</td>
										<td>{money(prior && prior[`median_asking_rent_${bed.key}`])}</td>
										<td>{change(`median_asking_rent_${bed.key}`)}</td>
										<td>{current[`rental_inventory_${bed.key}`] > 0 ? _formatNumber(current[`rental_inventory_${bed.key}`]) : '--'}</td>
									</tr>
								))}
							</tbody>
						</Table>
					</Panel>

					<Panel lg={12} xl={6} md={12} title={<Fragment>{label} — Median Asking Rent Trend <ToolTip title={`Monthly median asking rent across all rental listings in ${label} over the past five years. Not specific to this building.`} /></Fragment>}>
						<ResponsiveContainer height={260}>
							<LineChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
								<CartesianGrid strokeDasharray="3 3" />
								<XAxis dataKey="name" interval={Math.max(0, Math.ceil(chartData.length / 8) - 1)} tick={{ fontSize: 11 }} />
								<YAxis tickFormatter={v => `$${Math.round(v / 1000)}K`} tick={{ fontSize: 11 }} width={55} />
								<Tooltip formatter={v => [`$${_formatNumber(v)}`, 'Median Asking Rent']} />
								<Line type="monotone" dataKey="rent" stroke={getDefaultChartColors[0]} strokeWidth={2} dot={false} />
							</LineChart>
						</ResponsiveContainer>
					</Panel>
				</Row>

				<Row>
					<Col md={12}>
						<p className="small-text light-text" style={{ marginBottom: 20 }}>{SE_SOURCE_CREDIT}</p>
					</Col>
				</Row>
			</Fragment>
		);
	}
}

export default NeighborhoodRentMarket;
